/** Exact contract shapes for `/api/rtts/*` — do not invent fields. */

export type Vod = {
  vod_id: string;
  title: string;
  duration_sec: number;
  created_at: string;
  thumbnail_url: string;
  /** Twitch/Helix expiry if the API ever projects it. Absent → no countdown. */
  expires_at?: string | null;
  viewable_until?: string | null;
  /** Title-keyword guess from the backend. Absent on older backends. */
  rtts_game_guess?: 'madden' | 'cfb' | 'other' | 'unknown' | string;
  /**
   * False when the backend classified this VOD as an unsupported game.
   * Absent/undefined → eligible (older backends).
   */
  rtts_vod_eligible?: boolean;
};

export type ReelDurationTier = 'short' | 'mid' | 'long';

/**
 * Entitlement from GET /api/rtts/me/entitlement.
 * Free fields always present; paid period fields are additive when tier === "paid".
 * Admin allowlist may return tier "admin" + unlimited: true (additive).
 */
export type Entitlement = {
  tier: 'free' | 'paid' | 'admin' | 'comped' | string;
  reels_used: number;
  free_cap: number;
  /** null when unlimited (admin). */
  reels_remaining: number | null;
  unlimited?: boolean;
  /**
   * True only for an active Stripe-backed Pro sub. Never includes the
   * customer id. Absent on older backends — treat undefined as "show if paid".
   */
  can_manage_subscription?: boolean;
  /** Paid-period usage (additive). */
  reels_used_period?: number;
  period_cap?: number;
  period_reels_remaining?: number;
  period_resets_at?: string;
  /** Server flag: explicit metered re-cut via acquire { recut: true }. */
  recut_enabled?: boolean;
  /** Soft-beta: standalone locked to game recap (both teams). */
  recap_only?: boolean;
  /** Post-delivery Review & Trim (cheap re-render, no dock). */
  trim_enabled?: boolean;
  /** Full pool Customize (add + drop + trim). Requires trim_enabled. */
  curate_enabled?: boolean;
  /**
   * Delivered-reel Customize / Refine entry (`studio-customize-open`).
   * From `RTTS_CURATION_ENABLED` (default false).
   */
  customize_entry_enabled?: boolean;
  /**
   * User-uploaded music/voice. From `RTTS_UPLOAD_ADDONS_ENABLED` (default false).
   */
  upload_addons_enabled?: boolean;
  /** Render from stored detect (no Gemini). Default off on older backends. */
  render_from_cache_enabled?: boolean;
  /** Recut reuses source + candidates when cache render is on. */
  recut_reuse_source?: boolean;
  /** User-selectable 0:30 / 1:30 / up-to-2:30. */
  duration_tiers_enabled?: boolean;
  /** Pro Add-ons toggle to brand a finished reel. Default off on older backends. */
  watermark_opt_in_enabled?: boolean;
  /**
   * One-time reel-pack credit balance. Additive. 0 when none.
   * Does not change tier — pack buyers stay free.
   */
  reel_credits?: number;
  /** Pay-on-accept: dock at Accept; generate is watermarked preview. */
  signoff_billing_enabled?: boolean;
  accepted_used?: number;
  accepted_max?: number | null;
  generations_used?: number;
  generation_ceiling?: number | null;
};

export type HighlightJobStatus =
  | 'queued'
  | 'downloading'
  | 'source_ready'
  | 'detecting'
  | 'detect_ready'
  | 'awaiting_curation'
  | 'rendering'
  | 'render_ready'
  | 'delivered'
  | 'failed'
  | string;

/**
 * GET /api/rtts/me/reels item — exact backend library projection.
 * signed_url / expires_at / poster_url only when delivered & not expired.
 */
export type ReelSummary = {
  job_id: string;
  provider_vod_id: string;
  vod_title?: string | null;
  reel_name?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  signed_url?: string | null;
  expires_at?: string | null;
  poster_url?: string | null;
  reel_duration_sec?: number | null;
  error_code?: string | null;
  clip_count?: number | null;
  vod_created_at?: string | null;
  share_url?: string | null;
  /** True when the stored name is empty or the Untitled sentinel. */
  needs_reel_name?: boolean;
};

export type HighlightJob = {
  id: string;
  status: HighlightJobStatus;
  signed_url?: string | null;
  expires_at?: string | null;
  error_code?: string | null;
  reel_duration_sec?: number | null;
  /** User-selected length tier (short | mid | long). */
  reel_duration_tier?: ReelDurationTier | string | null;
  reel_duration_target_sec?: number | null;
  detect_reused?: boolean;
  /** Optional poster / VOD thumb for the player. */
  poster_url?: string | null;
  /** User-supplied reel name (never the Twitch VOD title). */
  reel_name?: string | null;
  curation_deadline_at?: string | null;
  /** Twitch VOD id when present on the job document. */
  provider_vod_id?: string | null;
  /** Pay-on-accept: watermarked preview until Accept. */
  watermark?: boolean;
  /** Pro branding choice — distinct from free-enforcement watermark. */
  watermark_opt_in?: boolean;
  /** Overlay job: applying | ready | failed. Toggle-ON is async. */
  watermark_status?: 'applying' | 'ready' | 'failed' | string | null;
  /** Music/voice mix job: applying | ready | failed. Apply is async. */
  mix_status?: 'applying' | 'ready' | 'failed' | string | null;
  /** Job document clock — used to remount the reel player after a remix. */
  updated_at?: string | null;
  accepted?: boolean;
  accepted_at?: string | null;
  signoff_preview?: boolean;
  /** Standalone music bed (Feature 4) — absent when no music. */
  music_id?: string | null;
  music_mode?: 'bed' | 'music_only' | string | null;
  music_has_vocals?: boolean;
  music_mode_promoted?: boolean;
  music_requested_mode?: 'bed' | 'music_only' | string | null;
  game_gain_db?: number | null;
  music_gain_db?: number | null;
  music_loop?: boolean | null;
  music_fade_in_s?: number | null;
  music_fade_out_s?: number | null;
  /** Post-delivery add-on: which MP4 play/share/download serve. */
  reel_active_version?: 'original' | 'music' | 'mix' | string | null;
  music_addon_status?: 'ready' | 'failed' | 'removed' | string | null;
  music_addon_level?: 'low' | 'med' | 'high' | string | null;
  music_volume?: number | null;
  reel_audio_volume?: number | null;
  voice_volume?: number | null;
  voice_source?: 'recorded' | 'uploaded' | string | null;
  voice_asset_id?: string | null;
  music_source?: 'library' | 'uploaded' | 'absent' | string | null;
  music_asset_id?: string | null;
  music_asset_name?: string | null;
  /** Fan-out detection window counters (read-only progress surfacing). */
  detect_windows_total?: number | null;
  detect_windows_done?: number | null;
  detect_windows_failed?: number | null;
  source_duration_sec?: number | null;
  created_at?: string | null;
  vod_created_at?: string | null;
  clip_count?: number | null;
  /** Permanent public watch URL (`https://scoutlytics.gg/r/<token>`). */
  share_url?: string | null;
  /** True when detect is waiting for a global Job slot (cap held, not burned). */
  detect_dispatch_deferred?: boolean;
};

export type CurationCandidate = {
  candidate_id: string;
  start_sec: number;
  end_sec: number;
  duration_sec: number;
  /** Alias of duration_sec from candidates API. */
  duration?: number;
  /** Detect snap-start — immutable shrink-only lower rail. */
  detected_start_sec?: number;
  /** Detect safe end — immutable shrink-only upper rail. */
  detected_safe_end_sec?: number;
  /** Active in/out for refine (defaults to detect bounds). */
  current_start_sec?: number;
  current_end_sec?: number;
  thumbnail_url?: string | null;
  label: string;
  why?: string;
  team?: string | null;
  spectacle_score?: number;
  /** Detect score when spectacle_score is absent. */
  score?: number;
  /** Detect camera/kind tag (`live` | `replay` | `cutscene` | `menu`) when present. */
  clip_kind?: string | null;
  is_live_play?: boolean | null;
  /** Play-type label from detect when present (TD/INT/Sack/…). */
  play_type?: string | null;
  selected_by_default?: boolean;
  in_current_reel?: boolean;
  /** Alias of in_current_reel. */
  in_reel?: boolean;
};

export type RefineClip = {
  candidate_id: string;
  start_sec: number;
  end_sec: number;
};

/** Optional music stamp for refine / accept (null music_id clears music). */
export type RefineMusicOptions = {
  music_id: string | null;
  music_mode: 'bed' | 'music_only';
  game_gain_db: number;
  music_gain_db: number;
  /** Loop bed to fill reel length (default true). */
  music_loop: boolean;
  /** Fade-in seconds on the music bed (0 = off). */
  music_fade_in_s: number;
  /** Fade-out seconds ending at reel end (0 = off). */
  music_fade_out_s: number;
};

export type CurationState = {
  status: string;
  deadline_at?: string | null;
  submitted_at?: string | null;
  auto_submitted?: boolean;
  selected?: string[];
  refine_count?: number;
  curate_enabled?: boolean;
  curate_max_clips?: number;
  curate_max_duration_sec?: number;
};

export type CandidatesSuccess = {
  candidates: CurationCandidate[];
  curation: CurationState;
  /** Short-TTL signed GET to the job source VOD (Part A). Ephemeral — do not persist. */
  source_preview_url?: string | null;
  source_preview_expires_at?: string | null;
  trim_enabled?: boolean;
  curate_enabled?: boolean;
};

export type AcquireSuccess = {
  job: HighlightJob;
};

export type AcquireErrorCode =
  | 'upgrade_required'
  | 'monthly_cap_reached'
  | 'reel_already_exists'
  | 'generation_ceiling_reached'
  | 'reel_name_required'
  | 'daily_capacity_reached'
  | 'not_allowlisted'
  | 'generation_paused'
  | 'vod_unsupported_game';

/** Auth/link states exercised by the mock layer. */
export type StudioMockAuthState = 'logged_out' | 'linked' | 'stale_link';

/** Dev-only scenario switch for acquire/entitlement/job/checkout branches. */
export type StudioMockScenario =
  | 'free_ok'
  | 'free_capped'
  | 'paid_ok'
  | 'paid_capped'
  | 'admin_ok'
  | 'already_exists'
  | 'fail'
  | 'checkout_pending'
  | 'reels_empty'
  | 'vods_empty'
  | 'vods_unsupported_game';

export type StudioCheckoutPlan = 'monthly' | 'annual' | 'pack5';

export type CheckoutSuccess = {
  checkout_url: string;
};

/** GET/PUT /api/rtts/me/profile */
export type Profile = {
  twitch_handle: string | null;
  avatar_url: string | null;
  display_name: string | null;
  email: string | null;
  onboarded: boolean;
};

export type ProfileUpdate = {
  display_name?: string | null;
  email?: string | null;
  onboarded?: boolean;
};

/** POST /api/rtts/billing/portal */
export type PortalSuccess = {
  portal_url: string;
};

export type PortalErrorCode =
  | 'no_active_subscription'
  | 'portal_failed'
  | 'billing_unavailable';

/** Error thrown by the client when Twitch link is stale (401). */
export class TwitchSignInRequiredError extends Error {
  readonly status = 401;
  readonly code = 'sign_in_with_twitch';

  constructor(message = 'sign in with Twitch') {
    super(message);
    this.name = 'TwitchSignInRequiredError';
  }
}

export function isTwitchSignInRequiredError(
  error: unknown
): error is TwitchSignInRequiredError {
  return error instanceof TwitchSignInRequiredError;
}

/** POST /api/rtts/highlights/jobs/{id}/rerender-duration failures. */
export type DurationRerenderErrorCode =
  | 'source_unavailable'
  | 'not_ready_to_rerender'
  | 'feature_disabled'
  | 'not_found'
  | 'failed';

export class DurationRerenderError extends Error {
  readonly status: number;
  readonly error: DurationRerenderErrorCode;

  constructor(
    error: DurationRerenderErrorCode,
    options: { status: number; message?: string }
  ) {
    super(options.message ?? error);
    this.name = 'DurationRerenderError';
    this.status = options.status;
    this.error = error;
  }
}

export function isDurationRerenderError(
  error: unknown
): error is DurationRerenderError {
  return error instanceof DurationRerenderError;
}

/**
 * Distinct acquire failure carrying status + error code.
 * Distinguishes 402 upgrade_required vs monthly_cap_reached, and 409 already_exists.
 */
export class AcquireError extends Error {
  readonly status: number;
  readonly error: AcquireErrorCode;
  readonly period_resets_at?: string;
  /** TODO(seam): confirm 409 body includes existing job_id. */
  readonly job_id?: string;
  readonly period_cap?: number;

  constructor(
    error: AcquireErrorCode,
    options: {
      status: number;
      message?: string;
      period_resets_at?: string;
      job_id?: string;
      period_cap?: number;
    }
  ) {
    super(options.message ?? error);
    this.name = 'AcquireError';
    this.status = options.status;
    this.error = error;
    this.period_resets_at = options.period_resets_at;
    this.job_id = options.job_id;
    this.period_cap = options.period_cap;
  }
}

export function isAcquireError(error: unknown): error is AcquireError {
  return error instanceof AcquireError;
}

export function isUpgradeRequiredError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'upgrade_required';
}

export function isMonthlyCapReachedError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'monthly_cap_reached';
}

export function isReelAlreadyExistsError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'reel_already_exists';
}

export function isGenerationCeilingError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'generation_ceiling_reached';
}

export function isReelNameRequiredError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'reel_name_required';
}

export function isDailyCapacityReachedError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'daily_capacity_reached';
}

export function isNotAllowlistedError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'not_allowlisted';
}

export function isGenerationPausedError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'generation_paused';
}

export function isUnsupportedGameError(error: unknown): error is AcquireError {
  return isAcquireError(error) && error.error === 'vod_unsupported_game';
}

/** PUT /api/rtts/me/profile → 400 invalid_email */
export class InvalidEmailError extends Error {
  readonly status = 400;
  readonly error = 'invalid_email' as const;

  constructor(message = 'Invalid email format.') {
    super(message);
    this.name = 'InvalidEmailError';
  }
}

export function isInvalidEmailError(error: unknown): error is InvalidEmailError {
  return error instanceof InvalidEmailError;
}

/**
 * POST /api/rtts/billing/portal failures (409 / 404 / 500).
 * UI maps all of these to one friendly inline message.
 */
export class PortalError extends Error {
  readonly status: number;
  readonly error: PortalErrorCode;

  constructor(
    error: PortalErrorCode,
    options: { status: number; message?: string }
  ) {
    super(options.message ?? error);
    this.name = 'PortalError';
    this.status = options.status;
    this.error = error;
  }
}

export function isPortalError(error: unknown): error is PortalError {
  return error instanceof PortalError;
}
